/**
 * The Convert dialog — `lead-detail.html`'s three-column Account / Contact /
 * Opportunity layout, ported.
 *
 * `POST /leads/{id}/convert` answers 501 (`leads.routes.js`'s
 * `LEAD_VERBS_NOT_BUILT`), so this always runs through `useConvertLead`'s mock
 * fallback today — but the body it sends is shaped exactly like `docs/API.md`
 * §4's documented contract, so nothing here has to change the day the endpoint
 * ships.
 *
 * "Choose Existing" is disabled on both Account and Contact: it needs a
 * type-ahead search against `GET /accounts` / `GET /contacts` that this dialog
 * does not wire up, and a select with one fake option is worse than a
 * disabled one that says why. The prototype ships the same asymmetry — its own
 * Contact picker is a disabled placeholder too.
 */
import { useEffect, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';

export function ConvertModal({ open, lead, options, busy, onClose, onConfirm }) {
  const [oppSkip, setOppSkip] = useState(false);
  const [oppName, setOppName] = useState('');
  const [oppAmount, setOppAmount] = useState('');
  const [oppClose, setOppClose] = useState('');
  const [contactName, setContactName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [moveActivities, setMoveActivities] = useState(true);

  useEffect(() => {
    if (!open || !lead) return;
    setOppSkip(false);
    setOppName(`${lead.company} — ${lead.industry && lead.industry !== '—' ? lead.industry : 'New business'}`);
    setOppAmount(lead.value ?? '');
    setOppClose('');
    setContactName(lead.name ?? '');
    setAccountName(lead.company ?? '');
    setOwnerId('');
    setStatusId('');
    setMoveActivities(true);
  }, [open, lead]);

  if (!lead) return null;

  const submit = () => {
    onConfirm({
      account: { mode: 'create', name: accountName },
      contact: { mode: 'create', is_primary: true, full_name: contactName },
      opportunity: oppSkip
        ? { mode: 'skip' }
        : { mode: 'create', name: oppName, amount: oppAmount === '' ? undefined : Number(oppAmount), currency: lead.currency, expected_close_date: oppClose || undefined },
      owner_id: ownerId || undefined,
      converted_status_id: statusId || undefined,
      move_activities: moveActivities,
      row_version: lead.raw?.row_version,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert Lead"
      size="lg"
      footer={
        <>
          <span className="t-xs ink-3 me-auto">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            Conversion history is retained and the lead becomes read-only.
          </span>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-add btn-sm-x" disabled={busy} onClick={submit}>
            {busy ? <><ButtonSpinner />Converting…</> : 'Convert'}
          </button>
        </>
      }
    >
      <div className="t-xs ink-3 mb-3">{lead.name} · {lead.lead_no}</div>

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-cool)' }}><i className="bi bi-building" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Account</div><div className="t-2xs ink-3">Company record</div></div>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="acct" id="acctExisting" disabled />
              <label className="form-check-label ink-4" htmlFor="acctExisting" title="Account search is not wired up in this build">Choose Existing</label>
            </div>
            <select className="form-select mb-2" disabled><option>Search accounts…</option></select>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="acct" id="acctNew" checked readOnly />
              <label className="form-check-label" htmlFor="acctNew">Create New</label>
            </div>
            <input className="form-control" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          </div>
        </div>

        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-mint)' }}><i className="bi bi-person" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Contact</div><div className="t-2xs ink-3">Person record</div></div>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="cont" id="contNew" checked readOnly />
              <label className="form-check-label" htmlFor="contNew">Create New</label>
            </div>
            <input className="form-control mb-2" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="cont" id="contExisting" disabled />
              <label className="form-check-label ink-4" htmlFor="contExisting" title="Contact search is not wired up in this build">Choose Existing</label>
            </div>
            <select className="form-select" disabled><option>Search contacts…</option></select>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-brand)' }}><i className="bi bi-briefcase" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Opportunity</div><div className="t-2xs ink-3">Deal record</div></div>
            </div>
            <input className="form-control mb-2" disabled={oppSkip} value={oppName} onChange={(e) => setOppName(e.target.value)} />
            <div className="row g-2">
              <div className="col-6">
                <label className="lbl">Amount</label>
                <input className="form-control" disabled={oppSkip} value={oppAmount} onChange={(e) => setOppAmount(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="lbl">Close Date</label>
                <input className="form-control" type="date" disabled={oppSkip} value={oppClose} onChange={(e) => setOppClose(e.target.value)} />
              </div>
            </div>
            <div className="form-check mt-2">
              <input className="form-check-input" type="checkbox" id="oppSkip" checked={oppSkip} onChange={(e) => setOppSkip(e.target.checked)} />
              <label className="form-check-label" htmlFor="oppSkip">Don&rsquo;t create an opportunity</label>
            </div>
          </div>
        </div>
      </div>

      <hr className="soft" />
      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <Field label="Record Owner">
            <select className="form-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">Leave as {lead.owner ?? 'unassigned'}</option>
              {options.owners.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Converted Status">
            <select className="form-select" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              <option value="">Tenant default</option>
              {options.statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="convActivities" checked={moveActivities} onChange={(e) => setMoveActivities(e.target.checked)} />
            <label className="form-check-label" htmlFor="convActivities">Carry over open activities</label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
